import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { formatQuestionSequence, shouldShowQuestionDisplayIdMeta } from '../utils/questionDisplay';
import type { PracticeNavigationTreeItem, PracticeNavigationTreeSection } from '../utils/navigationTree';
import type { PracticeQuestionFilter, PracticeQuestionFilterChangeHandler } from '../store/practiceShellStore';
import { studentText, useStudentLocale } from '@/app/providers/StudentLocaleProvider';
import { QuestionContentBadges } from '@/features/shared/components/QuestionContentBadges';

type Props = {
  sections: PracticeNavigationTreeSection[];
  onSelectQuestion: (questionId: string) => void;
  onOpenType?: (questionTypeCode: string) => void;
  compactSingleGroup?: boolean;
  expandAllByDefault?: boolean;
  expansionMode?: 'question' | 'type' | 'section';
  pageSizePerGroup?: number;
  showColumnHeader?: boolean;
  scrollToCurrentSignal?: string | number | boolean | null;
  layoutVariant?: 'sidebar' | 'drawer';
  showNavigationHeader?: boolean;
  navigationTitle?: string;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  typeItemHeightPx?: number;
  typeFontSizePx?: number;
  typeItemGapPx?: number;
  showSectionBorder?: boolean;
  showTypeBorder?: boolean;
  showQuestionBorder?: boolean;
  showTypeSelectedColor?: boolean;
  showHighFrequencyBadge?: boolean;
  questionBadgeStyle?: 'soft' | 'outline';
  navigationFilterEnabled?: boolean;
  navigationFilter?: PracticeQuestionFilter;
  navigationFilterShowFrequency?: boolean;
  navigationFilterShowNew?: boolean;
  onNavigationFilterChange?: PracticeQuestionFilterChangeHandler;
};

function NavigationFilterControl({
  enabled,
  value,
  showFrequency,
  showNew,
  onChange,
  useDrawerLayout,
}: {
  enabled: boolean;
  value: PracticeQuestionFilter;
  showFrequency: boolean;
  showNew: boolean;
  onChange?: PracticeQuestionFilterChangeHandler;
  useDrawerLayout: boolean;
}) {
  const locale = useStudentLocale();
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const radioName = `practice-navigation-frequency-${useId().replace(/:/g, '')}`;
  const hasFilter = enabled && (showFrequency || showNew) && Boolean(onChange);
  const activeFilterCount = [showFrequency && value.frequencyScope !== 'all', showNew && value.newOnly]
    .filter(Boolean)
    .length;
  const actionClass = useDrawerLayout
    ? 'hover:bg-[var(--color-practice-drawer-hover-bg)] hover:text-[var(--color-practice-drawer-active-fg)] focus-visible:outline-[var(--color-practice-drawer-current-ring)]'
    : 'hover:bg-[var(--color-shell-sidebar-hover-bg)] hover:text-[var(--color-shell-sidebar-active-fg)] focus-visible:outline-[var(--color-shell-sidebar-active-border)]';

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const width = Math.min(288, window.innerWidth - 24);
      const estimatedHeight = 310;
      const top = rect.bottom + 8 + estimatedHeight <= window.innerHeight
        ? rect.bottom + 8
        : Math.max(12, rect.top - estimatedHeight - 8);
      setPanelPosition({
        top,
        left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
      });
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && !buttonRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    updatePosition();
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  if (!hasFilter) {
    return null;
  }

  const updateFilter = (next: PracticeQuestionFilter) => {
    onChange?.(next);
  };
  const portalTarget = typeof document !== 'undefined'
    ? (buttonRef.current?.closest('.student-shell-theme') || document.body)
    : null;

  return (
    <div>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-sm)] px-2 text-[var(--color-text-secondary)] transition-colors focus-visible:outline focus-visible:outline-2 ${actionClass}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={studentText(locale, 'Filter questions', '筛选题目')}
        title={studentText(locale, 'Filter questions', '筛选题目')}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        {activeFilterCount ? (
          <span className="min-w-4 rounded-[var(--radius-full)] bg-[var(--color-primary-light)] px-1 text-center text-[10px] font-semibold leading-4 text-[var(--color-primary)]">
            {activeFilterCount}
          </span>
        ) : null}
      </button>
      {open && portalTarget
        ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={studentText(locale, 'Question filters', '题目筛选')}
            className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left shadow-[var(--shadow-lg)]"
            style={{
              position: 'fixed',
              top: panelPosition.top,
              left: panelPosition.left,
              zIndex: 1000,
              width: Math.min(288, window.innerWidth - 24),
              borderRadius: 'var(--radius-md)',
            }}
          >
            {showFrequency ? (
              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">
                  {studentText(locale, 'Frequency', '频率范围')}
                </legend>
                {([
                  ['all', studentText(locale, 'All questions', '全部题目')],
                  ['high_frequency', studentText(locale, 'High-frequency', '高频题')],
                  ['regular', studentText(locale, 'Non-high-frequency', '非高频题')],
                ] as const).map(([scope, label]) => (
                  <label key={scope} className="flex cursor-pointer items-center gap-2 py-1.5 text-sm text-[var(--color-text)]">
                    <input
                      type="radio"
                      name={radioName}
                      value={scope}
                      checked={value.frequencyScope === scope}
                      onChange={() => updateFilter({ ...value, frequencyScope: scope })}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>
            ) : null}
            {showNew ? (
              <label className={`${showFrequency ? 'mt-2 border-t border-[var(--color-border)] pt-3' : ''} flex cursor-pointer items-center gap-2 text-sm text-[var(--color-text)]`}>
                <input
                  type="checkbox"
                  checked={value.newOnly}
                  onChange={(event) => updateFilter({ ...value, newOnly: event.target.checked })}
                />
                {studentText(locale, 'Only new questions', '仅看新增')}
              </label>
            ) : null}
            <fieldset className="mt-3 border-t border-[var(--color-border)] pt-3">
              <legend className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">
                {studentText(locale, 'Applies to', '适用范围')}
              </legend>
              <label className="flex items-center gap-2 text-sm font-medium text-[var(--color-text)]">
                <input
                  type="radio"
                  name={`practice-navigation-scope-${radioName}`}
                  checked
                  readOnly
                  aria-label={studentText(locale, 'Only current question type', '仅对当前题型生效')}
                />
                {studentText(locale, 'Only current question type', '仅对当前题型生效')}
              </label>
              <p className="mt-1 pl-5 text-[11px] leading-4 text-[var(--color-text-secondary)]">
                {studentText(locale, 'Other question types keep their own filters.', '其他题型保留各自的筛选状态。')}
              </p>
            </fieldset>
            <div className="mt-4 flex items-center justify-between gap-3">
              {activeFilterCount ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-[var(--color-primary)]"
                  onClick={() => updateFilter({ frequencyScope: 'all', newOnly: false })}
                >
                  {studentText(locale, 'Clear filters', '清除筛选')}
                </button>
              ) : <span />}
              <button
                type="button"
                className="text-xs font-semibold text-[var(--color-text-secondary)]"
                onClick={() => setOpen(false)}
              >
                {studentText(locale, 'Done', '完成')}
              </button>
            </div>
          </div>,
          portalTarget,
        )
        : null}
    </div>
  );
}

function NavigationTreeActions({
  onLocateCurrent,
  onCollapseAll,
  useDrawerLayout,
  navigationFilterEnabled,
  navigationFilter,
  navigationFilterShowFrequency,
  navigationFilterShowNew,
  onNavigationFilterChange,
}: {
  onLocateCurrent: () => void;
  onCollapseAll: () => void;
  useDrawerLayout: boolean;
  navigationFilterEnabled: boolean;
  navigationFilter: PracticeQuestionFilter;
  navigationFilterShowFrequency: boolean;
  navigationFilterShowNew: boolean;
  onNavigationFilterChange?: PracticeQuestionFilterChangeHandler;
}) {
  const locale = useStudentLocale();
  const actionClass = useDrawerLayout
    ? 'hover:bg-[var(--color-practice-drawer-hover-bg)] hover:text-[var(--color-practice-drawer-active-fg)] focus-visible:outline-[var(--color-practice-drawer-current-ring)]'
    : 'hover:bg-[var(--color-shell-sidebar-hover-bg)] hover:text-[var(--color-shell-sidebar-active-fg)] focus-visible:outline-[var(--color-shell-sidebar-active-border)]';
  return (
    <div className="flex items-center gap-1">
      <NavigationFilterControl
        enabled={navigationFilterEnabled}
        value={navigationFilter}
        showFrequency={navigationFilterShowFrequency}
        showNew={navigationFilterShowNew}
        onChange={onNavigationFilterChange}
        useDrawerLayout={useDrawerLayout}
      />
      <button
        type="button"
        onClick={onLocateCurrent}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors focus-visible:outline focus-visible:outline-2 ${actionClass}`}
        aria-label={studentText(locale, 'Locate current question', '定位当前题目')}
        title={studentText(locale, 'Locate current question', '定位当前题目')}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="6.5" />
          <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onCollapseAll}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors focus-visible:outline focus-visible:outline-2 ${actionClass}`}
        aria-label={studentText(locale, 'Collapse all question groups', '折叠全部题型')}
        title={studentText(locale, 'Collapse all question groups', '折叠全部题型')}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m5 9 7 7 7-7" />
          <path d="m5 4 7 7 7-7" opacity="0.45" />
        </svg>
      </button>
    </div>
  );
}

function statusBadgeClass(progressStatus: string, attemptCount: number) {
  if (progressStatus === 'done') {
    return 'border-[var(--color-practice-status-done)] bg-[var(--color-practice-status-done-light)] text-[var(--color-practice-status-done)]';
  }
  if (attemptCount > 0) {
    return 'border-[var(--color-practice-status-tried)] bg-[var(--color-practice-status-tried-light)] text-[var(--color-practice-status-tried)]';
  }
  return 'border-[var(--color-border)] bg-[var(--color-practice-status-new-light)] text-[var(--color-practice-status-new)]';
}

function statusDotClass(progressStatus: string, attemptCount: number) {
  if (progressStatus === 'done') {
    return 'bg-[var(--color-practice-status-done)]';
  }
  if (attemptCount > 0) {
    return 'bg-[var(--color-practice-status-tried)]';
  }
  return 'bg-[var(--color-practice-status-new)]';
}

function statusLabel(progressStatus: string, attemptCount: number, locale: 'zh-CN' | 'en-US') {
  if (progressStatus === 'done') {
    return studentText(locale, 'Completed', '已完成');
  }
  if (attemptCount > 0) {
    return studentText(locale, 'In progress', '进行中');
  }
  return studentText(locale, 'Not started', '未练习');
}

function progressCountLabel(completedCount: number, totalCount: number) {
  return `${completedCount} / ${totalCount}`;
}

const drawerTitleClampStyle: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

function ColumnHeader() {
  const locale = useStudentLocale();
  return (
    <div className="grid grid-cols-[3rem_4.75rem_minmax(0,1fr)_4.5rem] items-center gap-3 rounded-[var(--radius-md)] bg-[var(--color-surface-muted)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">
      <span>{studentText(locale, 'No.', '序号')}</span>
      <span>{studentText(locale, 'ID', '编号')}</span>
      <span>{studentText(locale, 'Title', '题目')}</span>
      <span className="text-right">{studentText(locale, 'Status', '状态')}</span>
    </div>
  );
}

type NavigationItemButtonProps = {
  item: PracticeNavigationTreeItem;
  useDrawerLayout: boolean;
  showQuestionBorder: boolean;
  showHighFrequencyBadge: boolean;
  questionBadgeStyle: 'soft' | 'outline';
  onSelectQuestion: (questionId: string) => void;
  setItemRef: (questionId: string, element: HTMLButtonElement | null) => void;
};

function NavigationItemButton({
  item,
  useDrawerLayout,
  showQuestionBorder,
  showHighFrequencyBadge,
  questionBadgeStyle,
  onSelectQuestion,
  setItemRef,
}: NavigationItemButtonProps) {
  const locale = useStudentLocale();
  const showDisplayIdMeta = shouldShowQuestionDisplayIdMeta(item.titleDisplayMode);
  const metaLabel = showDisplayIdMeta ? item.displayId : item.questionTypeCode;
  const trailingLabel = useDrawerLayout
    ? (showDisplayIdMeta ? item.questionTypeCode : item.displayId)
    : null;

  if (useDrawerLayout) {
    return (
      <button
        ref={(element) => setItemRef(item.questionId, element)}
        type="button"
        onClick={() => onSelectQuestion(item.questionId)}
        className={`grid w-full grid-cols-[3rem_4.75rem_minmax(0,1fr)_4.5rem] items-start gap-3 rounded-[var(--radius-md)] px-3 py-2 text-left transition-colors ${showQuestionBorder ? 'border border-[var(--color-border)]' : ''} ${
          item.isCurrent
            ? 'bg-[var(--color-practice-drawer-active-bg)] text-[var(--color-practice-drawer-active-fg)] ring-1 ring-inset ring-[var(--color-practice-drawer-current-ring)]'
            : 'hover:bg-[var(--color-practice-drawer-hover-bg)]'
        }`}
      >
        <span className="mt-0.5 shrink-0 text-[11px] font-semibold tabular-nums text-[var(--color-text-secondary)]">
          {formatQuestionSequence(item.typeIndex)}
        </span>
        <span className="truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-secondary)]">
          {item.displayId}
        </span>
        <div className="min-w-0">
          <div className="flex min-w-0 items-start gap-1.5">
            <div
              className={`min-w-0 flex-1 text-sm leading-5 ${item.isCurrent ? 'font-semibold text-[var(--color-practice-drawer-active-fg)]' : 'text-[var(--color-text)]'}`}
              style={drawerTitleClampStyle}
            >
              {item.title}
            </div>
            {showHighFrequencyBadge && item.isHighFrequency ? (
              <QuestionContentBadges
                isHighFrequency
                style={questionBadgeStyle}
                locale={locale}
                className="shrink-0"
              />
            ) : null}
          </div>
          {!shouldShowQuestionDisplayIdMeta(item.titleDisplayMode) ? (
            <div className="mt-1 truncate text-[11px] text-[var(--color-text-secondary)]">
              {item.questionTypeCode}
            </div>
          ) : null}
        </div>
        <span className={`inline-flex min-h-6 items-center justify-center rounded-[var(--radius-full)] border px-2 text-[11px] font-semibold ${statusBadgeClass(item.progressStatus, item.attemptCount)}`}>
          {statusLabel(item.progressStatus, item.attemptCount, locale)}
        </span>
      </button>
    );
  }

  return (
    <button
      ref={(element) => setItemRef(item.questionId, element)}
      type="button"
      onClick={() => onSelectQuestion(item.questionId)}
      className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-3 rounded-[var(--radius-md)] px-3 py-2 text-left transition-colors ${showQuestionBorder ? 'border border-[var(--color-border)]' : ''} ${
        item.isCurrent
          ? 'bg-[var(--color-shell-sidebar-active-bg)] text-[var(--color-shell-sidebar-active-fg)] ring-1 ring-inset ring-[var(--color-shell-sidebar-active-border)]'
          : 'hover:bg-[var(--color-shell-sidebar-hover-bg)]'
      }`}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-secondary)]">
          <span className="tabular-nums text-[var(--color-text)]">
            {formatQuestionSequence(item.typeIndex)}
          </span>
          <span aria-hidden="true" className="text-[var(--color-text-tertiary)]">
            /
          </span>
          <span className="truncate">
            {metaLabel}
          </span>
          {showHighFrequencyBadge && item.isHighFrequency ? (
            <QuestionContentBadges
              isHighFrequency
              style={questionBadgeStyle}
              locale={locale}
            />
          ) : null}
        </div>
        <div
          className={`mt-1 break-words text-[13px] leading-5 ${item.isCurrent ? 'font-semibold text-[var(--color-shell-sidebar-active-fg)]' : 'text-[var(--color-text)]'}`}
        >
          {item.title}
        </div>
        {trailingLabel ? (
          <div className="mt-1 truncate text-[11px] text-[var(--color-text-secondary)]">
            {trailingLabel}
          </div>
        ) : null}
      </div>
      <span
        className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-[var(--radius-full)] ${statusDotClass(item.progressStatus, item.attemptCount)}`}
        aria-label={statusLabel(item.progressStatus, item.attemptCount, locale)}
        title={statusLabel(item.progressStatus, item.attemptCount, locale)}
      />
    </button>
  );
}

export function PracticeQuestionNavigationTree({
  sections,
  onSelectQuestion,
  onOpenType,
  compactSingleGroup = false,
  expandAllByDefault = false,
  expansionMode = 'question',
  pageSizePerGroup,
  showColumnHeader = false,
  scrollToCurrentSignal,
  layoutVariant = 'sidebar',
  showNavigationHeader = false,
  navigationTitle = 'Questions',
  onLoadMore,
  loadingMore = false,
  typeItemHeightPx = 40,
  typeFontSizePx = 14,
  typeItemGapPx = 12,
  showSectionBorder = true,
  showTypeBorder = true,
  showQuestionBorder = false,
  showTypeSelectedColor = true,
  showHighFrequencyBadge = false,
  questionBadgeStyle = 'soft',
  navigationFilterEnabled = false,
  navigationFilter = { frequencyScope: 'all', newOnly: false },
  navigationFilterShowFrequency = false,
  navigationFilterShowNew = false,
  onNavigationFilterChange,
}: Props) {
  const locale = useStudentLocale();
  const itemRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const useDrawerLayout = showColumnHeader || layoutVariant === 'drawer';
  const flattenedGroups = useMemo(
    () => sections.flatMap((section) => section.typeGroups.map((group) => ({ section, group }))),
    [sections],
  );
  const currentQuestionId = useMemo(
    () => sections
      .flatMap((section) => section.typeGroups.flatMap((group) => group.items))
      .find((item) => item.isCurrent)?.questionId || null,
    [sections],
  );
  const useCompactSingleGroup = compactSingleGroup && flattenedGroups.length === 1;
  const navigationProgress = useMemo(
    () => sections.reduce(
      (totals, section) => ({
        completed: totals.completed + section.completedCount,
        total: totals.total + section.totalCount,
      }),
      { completed: 0, total: 0 },
    ),
    [sections],
  );
  const currentSectionKeys = useMemo(
    () => sections.filter((section) => section.typeGroups.some((group) => group.isCurrentType)).map((section) => section.sectionKey),
    [sections],
  );
  const currentGroupKeys = useMemo(
    () => sections.flatMap((section) =>
      section.typeGroups
        .filter((group) => group.isCurrentType)
        .map((group) => `${section.sectionKey}:${group.questionTypeCode}`),
    ),
    [sections],
  );
  const allSectionKeys = useMemo(
    () => sections.map((section) => section.sectionKey),
    [sections],
  );
  const allGroupKeys = useMemo(
    () => sections.flatMap((section) => section.typeGroups.map((group) => `${section.sectionKey}:${group.questionTypeCode}`)),
    [sections],
  );
  const [expandedSectionKeys, setExpandedSectionKeys] = useState<string[]>(
    expandAllByDefault
      ? allSectionKeys
      : expansionMode === 'section'
        ? []
        : currentSectionKeys,
  );
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<string[]>(
    expandAllByDefault || expansionMode === 'question' ? currentGroupKeys : [],
  );
  const [visibleCountsByGroup, setVisibleCountsByGroup] = useState<Record<string, number>>({});

  const locateCurrentQuestion = () => {
    if (expansionMode === 'section') {
      setExpandedSectionKeys([]);
      setExpandedGroupKeys([]);
    } else {
      setExpandedSectionKeys(expandAllByDefault ? allSectionKeys : currentSectionKeys);
      setExpandedGroupKeys(expandAllByDefault || expansionMode === 'question' ? currentGroupKeys : []);
    }
    window.requestAnimationFrame(() => {
      const currentItemNode = currentQuestionId ? itemRefs.current.get(currentQuestionId) : null;
      currentItemNode?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  const collapseAllQuestions = () => {
    setExpandedSectionKeys([]);
    setExpandedGroupKeys([]);
  };

  useEffect(() => {
    if (expansionMode === 'section') {
      setExpandedSectionKeys([]);
      setExpandedGroupKeys([]);
      return;
    }
    setExpandedSectionKeys(expandAllByDefault ? allSectionKeys : currentSectionKeys);
    setExpandedGroupKeys(expandAllByDefault || expansionMode === 'question' ? currentGroupKeys : []);
  }, [allSectionKeys, currentGroupKeys, currentSectionKeys, expandAllByDefault, expansionMode]);

  useEffect(() => {
    if (!pageSizePerGroup || pageSizePerGroup <= 0) {
      setVisibleCountsByGroup({});
      return;
    }
    setVisibleCountsByGroup((current) => {
      const next = { ...current };
      let changed = false;
      allGroupKeys.forEach((groupKey) => {
        if (!next[groupKey] || next[groupKey] < pageSizePerGroup) {
          next[groupKey] = pageSizePerGroup;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [allGroupKeys, pageSizePerGroup]);

  const resolveVisibleCount = (groupKey: string, totalItems: number) => {
    if (onLoadMore) {
      return totalItems;
    }
    if (!pageSizePerGroup || pageSizePerGroup <= 0) {
      return totalItems;
    }
    return Math.min(totalItems, visibleCountsByGroup[groupKey] || pageSizePerGroup);
  };

  const showMoreItems = (groupKey: string, totalItems: number) => {
    if (!pageSizePerGroup || pageSizePerGroup <= 0) {
      return;
    }
    setVisibleCountsByGroup((current) => ({
      ...current,
      [groupKey]: Math.min(totalItems, (current[groupKey] || pageSizePerGroup) + pageSizePerGroup),
    }));
  };

  useEffect(() => {
    if (expansionMode === 'section') {
      setExpandedSectionKeys([]);
      setExpandedGroupKeys([]);
      return;
    }
    if (!expandAllByDefault) {
      return;
    }
    setExpandedSectionKeys(allSectionKeys);
    setExpandedGroupKeys(allGroupKeys);
  }, [allGroupKeys, allSectionKeys, expandAllByDefault, expansionMode]);

  useEffect(() => {
    if (expandAllByDefault || expansionMode === 'section') {
      return;
    }
    if (currentSectionKeys.length) {
      setExpandedSectionKeys(currentSectionKeys);
    }
  }, [currentSectionKeys, expandAllByDefault, expansionMode]);

  useEffect(() => {
    if (expandAllByDefault || expansionMode !== 'question') {
      setExpandedGroupKeys([]);
      return;
    }
    if (currentGroupKeys.length) {
      setExpandedGroupKeys(currentGroupKeys);
    }
  }, [currentGroupKeys, expandAllByDefault, expansionMode]);

  useEffect(() => {
    if (!currentQuestionId) {
      return;
    }
    const currentItemNode = itemRefs.current.get(currentQuestionId);
    if (!currentItemNode) {
      return;
    }
    const timer = window.requestAnimationFrame(() => {
      currentItemNode.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
    return () => window.cancelAnimationFrame(timer);
  }, [currentQuestionId, expandedGroupKeys, expandedSectionKeys, scrollToCurrentSignal]);

  const setItemRef = (questionId: string, element: HTMLButtonElement | null) => {
    if (!element) {
      itemRefs.current.delete(questionId);
      return;
    }
    itemRefs.current.set(questionId, element);
  };

  if (useCompactSingleGroup) {
    const [{ group }] = flattenedGroups;
    const compactGroupKey = `${sections[0]?.sectionKey || 'section'}:${group.questionTypeCode}`;
    const compactVisibleCount = resolveVisibleCount(compactGroupKey, group.items.length);
    const compactVisibleItems = group.items.slice(0, compactVisibleCount);
    return (
      <div className={useDrawerLayout ? 'flex h-full min-h-0 flex-col' : 'space-y-2'}>
        <div className={`flex items-center justify-between gap-3 pb-2 ${useDrawerLayout ? 'shrink-0' : ''}`}>
          <div className="min-w-0">
            <div className="break-words text-sm font-semibold leading-5 text-[var(--color-text)]">
              {group.typeName}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className="shrink-0 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-text-secondary)]"
              aria-label={`Completed ${group.completedCount} of ${group.totalCount}`}
              title={`Completed ${group.completedCount} / Total ${group.totalCount}`}
            >
              {progressCountLabel(group.completedCount, group.totalCount)}
            </span>
            <NavigationTreeActions
              onLocateCurrent={locateCurrentQuestion}
              onCollapseAll={collapseAllQuestions}
              useDrawerLayout={useDrawerLayout}
              navigationFilterEnabled={navigationFilterEnabled}
              navigationFilter={navigationFilter}
              navigationFilterShowFrequency={navigationFilterShowFrequency}
              navigationFilterShowNew={navigationFilterShowNew}
              onNavigationFilterChange={onNavigationFilterChange}
            />
          </div>
        </div>
        <div className={useDrawerLayout ? 'shrink-0' : ''}>
          {showColumnHeader ? <ColumnHeader /> : null}
        </div>
        <div className={useDrawerLayout ? 'student-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pt-1' : 'space-y-1'}>
          <div className="space-y-1">
            {compactVisibleItems.length ? compactVisibleItems.map((item) => (
              <NavigationItemButton
                key={item.questionId}
                item={item}
                useDrawerLayout={useDrawerLayout}
                showQuestionBorder={showQuestionBorder}
                showHighFrequencyBadge={showHighFrequencyBadge}
                questionBadgeStyle={questionBadgeStyle}
                onSelectQuestion={onSelectQuestion}
                setItemRef={setItemRef}
              />
            )) : (
              <div className="px-3 py-8 text-center text-sm text-[var(--color-text-secondary)]" role="status">
                {studentText(locale, 'No questions match the current filters.', '当前筛选条件下暂无题目。')}
              </div>
            )}
            {compactVisibleCount < group.totalCount ? (
              <button
                type="button"
                onClick={() => {
                  if (onLoadMore) {
                    onLoadMore();
                    return;
                  }
                  showMoreItems(compactGroupKey, group.items.length);
                }}
                disabled={loadingMore}
                className="inline-flex h-10 w-full items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface)]"
              >
                {loadingMore
                  ? studentText(locale, 'Loading...', '正在加载...')
                  : studentText(locale, `Show more (${Math.max(0, group.totalCount - compactVisibleCount)} remaining)`, `加载更多（剩余 ${Math.max(0, group.totalCount - compactVisibleCount)} 题）`)}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={showNavigationHeader ? 'flex h-full min-h-0 flex-col' : ''}>
      {showNavigationHeader ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-1 pb-3" role="toolbar" aria-label="Question navigation controls">
          <div className="min-w-0 flex-1 whitespace-nowrap text-sm font-semibold text-[var(--color-text)]">
            {navigationTitle}
          </div>
          <span
            className="shrink-0 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--color-text-secondary)]"
            aria-label={`Completed ${navigationProgress.completed} of ${navigationProgress.total}`}
            title={`Completed ${navigationProgress.completed} / Total ${navigationProgress.total}`}
          >
            {progressCountLabel(navigationProgress.completed, navigationProgress.total)}
          </span>
          <NavigationTreeActions
            onLocateCurrent={locateCurrentQuestion}
            onCollapseAll={collapseAllQuestions}
            useDrawerLayout={useDrawerLayout}
            navigationFilterEnabled={navigationFilterEnabled}
            navigationFilter={navigationFilter}
            navigationFilterShowFrequency={navigationFilterShowFrequency}
            navigationFilterShowNew={navigationFilterShowNew}
            onNavigationFilterChange={onNavigationFilterChange}
          />
        </div>
      ) : null}
      <div className={showNavigationHeader ? 'student-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pt-3' : ''}>
        <div className="space-y-4">
          {!showNavigationHeader ? (
            <div className="flex items-center justify-end gap-1">
              <NavigationTreeActions
                onLocateCurrent={locateCurrentQuestion}
                onCollapseAll={collapseAllQuestions}
                useDrawerLayout={useDrawerLayout}
                navigationFilterEnabled={navigationFilterEnabled}
                navigationFilter={navigationFilter}
                navigationFilterShowFrequency={navigationFilterShowFrequency}
                navigationFilterShowNew={navigationFilterShowNew}
                onNavigationFilterChange={onNavigationFilterChange}
              />
            </div>
          ) : null}
      {sections.map((section) => {
        const sectionExpanded = expandedSectionKeys.includes(section.sectionKey);
        const showSectionChildren = expansionMode !== 'section' && sectionExpanded;
        return (
          <section key={section.sectionKey} className={`${showSectionBorder ? 'border-b border-[var(--color-border)]' : ''} pb-4 last:border-b-0 last:pb-0`}>
            <button
              type="button"
              onClick={() => setExpandedSectionKeys((current) => (
                current.includes(section.sectionKey)
                  ? current.filter((item) => item !== section.sectionKey)
                  : [...current, section.sectionKey]
              ))}
              className={`flex w-full items-center justify-between gap-3 rounded-[var(--radius-md)] px-2 py-2 text-left transition-colors ${
                section.isCurrentSection
                  ? useDrawerLayout
                    ? 'bg-[var(--color-practice-drawer-active-bg)] text-[var(--color-practice-drawer-active-fg)]'
                    : 'bg-[var(--color-shell-sidebar-active-bg)] text-[var(--color-shell-sidebar-active-fg)]'
                  : useDrawerLayout
                    ? 'text-[var(--color-text)] hover:bg-[var(--color-practice-drawer-hover-bg)]'
                    : 'text-[var(--color-text)] hover:bg-[var(--color-shell-sidebar-hover-bg)]'
              }`}
            >
              <div className="min-w-0">
                <div className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${
                  section.isCurrentSection
                    ? (useDrawerLayout ? 'text-[var(--color-practice-drawer-active-fg)]' : 'text-[var(--color-shell-sidebar-active-fg)]')
                    : 'text-[var(--color-text-secondary)]'
                }`} aria-label={`Completed ${section.completedCount} of ${section.totalCount}`} title={`Completed ${section.completedCount} / Total ${section.totalCount}`}>
                  {section.sectionLabel}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`shrink-0 rounded-[var(--radius-full)] px-2 py-0.5 text-[11px] font-semibold ${
                  section.isCurrentSection
                    ? useDrawerLayout
                      ? 'bg-[var(--color-surface-raised)] text-[var(--color-practice-drawer-active-fg)]'
                      : 'bg-[var(--color-surface)] text-[var(--color-shell-sidebar-active-fg)]'
                    : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
                }`}>
                  {progressCountLabel(section.completedCount, section.totalCount)}
                </span>
                {expansionMode !== 'section' ? <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`text-[var(--color-text-secondary)] transition-transform ${sectionExpanded ? 'rotate-90' : ''}`}
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg> : null}
              </div>
            </button>
            {showSectionChildren ? (
              <div className="mt-3 flex flex-col" style={{ gap: `${typeItemGapPx}px` }}>
                {section.typeGroups.map((group) => {
                  const groupKey = `${section.sectionKey}:${group.questionTypeCode}`;
                  const groupExpanded = expandedGroupKeys.includes(groupKey);
                  const visibleCount = resolveVisibleCount(groupKey, group.items.length);
                  const visibleItems = group.items.slice(0, visibleCount);
                  const useSidebarSelectedColor = group.isCurrentType && !useDrawerLayout && showTypeSelectedColor;
                  return (
                    <div
                      key={groupKey}
                      className={`rounded-[var(--radius-lg)] ${showTypeBorder ? 'border' : ''} ${
                        group.isCurrentType
                          ? useDrawerLayout
                            ? `${showTypeBorder ? 'border-[var(--color-practice-drawer-active-border)]' : ''} bg-[var(--color-practice-drawer-active-bg)]`
                            : useSidebarSelectedColor
                              ? `${showTypeBorder ? 'border-[var(--color-shell-sidebar-type-selected-border)]' : ''} bg-[var(--color-shell-sidebar-type-selected-bg)]`
                              : `${showTypeBorder ? 'border-[var(--color-border)]' : ''} bg-[var(--color-surface)]`
                          : useDrawerLayout
                            ? `${showTypeBorder ? 'border-[var(--color-border)]' : ''} bg-[var(--color-surface-raised)]`
                            : `${showTypeBorder ? 'border-[var(--color-border)]' : ''} bg-[var(--color-surface)]`
                      }`}
                    >
                      <button
                        type="button"
                        aria-current={group.isCurrentType ? 'true' : undefined}
                        onClick={() => {
                          if (expansionMode === 'type') {
                            onOpenType?.(group.questionTypeCode);
                            return;
                          }
                          setExpandedGroupKeys((current) => (
                            current.includes(groupKey)
                              ? current.filter((item) => item !== groupKey)
                              : [...current, groupKey]
                          ));
                        }}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                          useDrawerLayout
                            ? 'focus-visible:outline-[var(--color-practice-drawer-current-ring)]'
                            : 'focus-visible:outline-[var(--color-shell-sidebar-active-border)]'
                        }`}
                        style={{ minHeight: `${typeItemHeightPx}px` }}
                      >
                        <div className="min-w-0">
                          <div className={`break-words text-sm font-semibold leading-5 ${
                            group.isCurrentType
                              ? useDrawerLayout
                                ? 'text-[var(--color-practice-drawer-active-fg)]'
                                : showTypeSelectedColor
                                  ? 'text-[var(--color-shell-sidebar-type-selected-fg)]'
                                  : 'font-bold text-[var(--color-text)]'
                              : 'text-[var(--color-text)]'
                          }`}>
                            <span className="inline-flex items-center gap-2" style={{ fontSize: `${typeFontSizePx}px` }}>
                              {group.isCurrentType && !useDrawerLayout && !showTypeSelectedColor ? (
                                <span className="h-2 w-2 shrink-0 rotate-45 border border-current" aria-hidden="true" />
                              ) : null}
                              <span>{group.typeName}</span>
                            </span>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className={`shrink-0 rounded-[var(--radius-full)] bg-[var(--color-surface)] px-2 py-0.5 text-[11px] font-semibold ${
                            group.isCurrentType
                              ? useDrawerLayout
                                ? 'text-[var(--color-practice-drawer-active-fg)]'
                                : showTypeSelectedColor
                                  ? 'text-[var(--color-shell-sidebar-type-selected-fg)]'
                                  : 'text-[var(--color-text-secondary)]'
                              : 'text-[var(--color-text-secondary)]'
                          }`} aria-label={`Completed ${group.completedCount} of ${group.totalCount}`} title={`Completed ${group.completedCount} / Total ${group.totalCount}`}>
                            {progressCountLabel(group.completedCount, group.totalCount)}
                          </span>
                          {expansionMode !== 'type' ? <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className={`h-3.5 w-3.5 shrink-0 text-[var(--color-text-secondary)] transition-transform ${groupExpanded ? 'rotate-90' : ''}`}
                            aria-hidden="true"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg> : <svg className="h-3.5 w-3.5 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 7h10v10H7z" /><path d="M4 4h10" /></svg>}
                        </div>
                      </button>
                      {groupExpanded ? (
                        <div className="space-y-1 px-2 pb-2">
                          {showColumnHeader ? <ColumnHeader /> : null}
                          {visibleItems.map((item) => (
                            <NavigationItemButton
                              key={item.questionId}
                              item={item}
                              useDrawerLayout={useDrawerLayout}
                              showQuestionBorder={showQuestionBorder}
                              showHighFrequencyBadge={showHighFrequencyBadge}
                              questionBadgeStyle={questionBadgeStyle}
                              onSelectQuestion={onSelectQuestion}
                              setItemRef={setItemRef}
                            />
                          ))}
                           {visibleCount < group.totalCount ? (
                             <button
                               type="button"
                               onClick={() => {
                                 if (onLoadMore) {
                                   onLoadMore();
                                   return;
                                 }
                                 showMoreItems(groupKey, group.items.length);
                               }}
                               disabled={loadingMore}
                               className="inline-flex h-10 w-full items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)]"
                             >
                               {loadingMore
                                 ? studentText(locale, 'Loading...', '正在加载...')
                                 : studentText(locale, `Show more (${Math.max(0, group.totalCount - visibleCount)} remaining)`, `加载更多（剩余 ${Math.max(0, group.totalCount - visibleCount)} 题）`)}
                             </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
        </div>
      </div>
    </div>
  );
}
